import React, { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../context/UserContext";
import Filter from "../components/layout/Filter";
import OverviewTab from "../features/stations/OverviewTab";
import SessionsTab from "../features/stations/SessionsTab";
import ChargersTab from "../features/stations/ChargersTab";
import { getStationsApi } from "../api/stationApi/getStationsApi";
import "./ViewDetails.scss";
import ExportModal from "../features/stations/ExportModalProps";

type Tab = "overview" | "sessions" | "chargers";

const ViewDetails: React.FC = () => {
  const { user } = useUser();
  const { stationId } = useParams<{ stationId: string }>();
  const navigate = useNavigate();

  const [showExportModal, setShowExportModal] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
  });

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [stationInfo, setStationInfo] = useState({
    name: "",
    address: "",
    status: "online" as "online" | "offline" | "maintenance",
  });

  const [allTabsData, setAllTabsData] = useState<{
    overview?: any;
    sessions?: any;
    chargers?: any;
  }>({});

  const formatLocalDate = (date: Date, endOfDay = false) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const time = endOfDay ? "23:59:59" : "00:00:00";
    return `${year}-${month}-${day}T${time}`;
  };

  const fetchTabData = async (tab: Tab) => {
    if (!user?.org_id || !stationId) return;
    setLoading(true);
    try {
      const response = await getStationsApi({
        tenantId: user.org_id,
        stationId,
        tab,
        startDate: formatLocalDate(dateRange.startDate, false),
        endDate: formatLocalDate(dateRange.endDate, true),
      });

      if (tab === "overview") {
        setStationInfo({
          name: response?.station.name,
          address: response?.station.address,
          status: response?.station.status,
        });
      }

      setData(response);

      setAllTabsData((prev) => ({
        ...prev,
        [tab]: response,
      }));
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.org_id || !stationId) return;
    fetchTabData(activeTab);
  }, [user, stationId, dateRange, activeTab]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    // fetchTabData(tab);
  };

  const handleApplyFilter = (start: Date, end: Date) => {
    setDateRange({ startDate: start, endDate: end });
  };

  // const handleExportClick = async () => {
  //   const tabs: Tab[] = ["overview", "sessions", "chargers"];

  //   for (const tab of tabs) {
  //     if (!allTabsData[tab]) {
  //       try {
  //         if (!user?.org_id || !stationId) return;
  //         const response = await getStationsApi({
  //           tenantId: user.org_id!,
  //           stationId: stationId!,
  //           tab,
  //           startDate: formatLocalDate(dateRange.startDate, false),
  //           endDate: formatLocalDate(dateRange.endDate, true),
  //         });

  //         setAllTabsData((prev) => ({
  //           ...prev,
  //           [tab]: response,
  //         }));
  //       } catch (err) {
  //         console.error(`Failed to fetch ${tab} data:`, err);
  //       }
  //     }
  //   }

  //   setShowExportModal(true);
  // };

  return (
    <div className="viewDetails-dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate("/stations")}>
            <ArrowLeft size={20} />
          </button>
          <div className="station-info">
            <h1>{stationInfo.name}</h1>
            <p>{stationInfo.address}</p>
          </div>
        </div>
        <div className="header-right">
          <button className={`status-badge ${stationInfo.status || "online"}`}>
            {(stationInfo.status || "online").charAt(0).toUpperCase() +
              (stationInfo.status || "online").slice(1)}
          </button>

          {/* {activeTab === "chargers" && (
            <button className="add-charger-button">Add charger</button>
          )}
          <button 
            className="export-button"
            onClick={handleExportClick}
          >
            Export report
          </button> */}
        </div>
      </header>

      <div className="tabs">
        <div className="select-tabs">
          {(["overview", "sessions", "chargers"] as Tab[]).map((tab) => (
            <button
              key={tab}
              className={`tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => handleTabChange(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <div className="filter-button">
          <Filter
            defaultStart={dateRange.startDate}
            defaultEnd={dateRange.endDate}
            onApply={handleApplyFilter}
          />
        </div>
      </div>

      {loading && (
        <div className="loader-container">
          <div className="loader" />
        </div>
      )}

      {!loading && data && activeTab === "overview" && (
        <OverviewTab data={data} />
      )}
      {!loading && data && activeTab === "sessions" && (
        <SessionsTab data={data} />
      )}
      {!loading && data && activeTab === "chargers" && (
        <ChargersTab data={data} />
      )}

      {/* <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        stationData={allTabsData}
        onExport={handleExport}
      /> */}
    </div>
  );
};

export default ViewDetails;
